function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
/*
 * Audiences > Health (redesign 2026-10-09, PlunkDocs mockups/audiences-redesign-2026-10-09.html board "4 · Health").
 *
 *   GET  /v1/audience/health?range=30 > ListHealth {members, withEmail, deliverable, deliverablePct, sent, bounced,
 *        complained, bounceRate, complaintRate, inactive, inactivePct, duplicatesPending, suppressedByReason{},
 *        optedIn, optedOut, legacyAllowed, weekly:[{weekStart, adds, unsubscribes, suppressions}],
 *        topDomains:[{domain,count,share}], flags[], notes[]}
 *   POST /v1/audience/purge/inactive {days, keepRecentOrderMonths, keepBusiness, mode: preview|demote}
 *        > {mode, days, candidates, keptRecentOrder, keptBusiness, alreadyInactive, demoted, reactivated,
 *           inactiveAudienceId, inactiveTotal, personIds[], notes[]}
 *
 * Layout: one summary card (verdict + three numbers), a checklist (one row per check, problems first,
 * at most one action each) and a right column (joined/left by week, email permission, the inactivity setting).
 * Rates arrive as fractions or percents depending on the build; frac() normalizes both.
 */
import React from "react";
import Chart from "../../components/chart";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Empty, Pending } from "../../components/analytics-kit";
import Error from "../../components/error";
import { num } from "../sales/territory-common";
import AudiencesShell, { SUBTITLES } from "./shell";
import { getHealth, purgeInactive, toast, getEmailSettings, putInactivityDays, isAdministrator } from "./api";
import { Modal, Check, FunnelRow, ErrorLine } from "./ui";
const RANGE = 30;
const frac = v => {
  const n = Number(v);
  return !isFinite(n) ? 0 : n <= 1 ? n : n / 100;
};
const pctText = (v, digits = 1) => {
  const p = frac(v) * 100;
  if (!isFinite(p)) return "0%";
  const d = p > 0 && p < 1 ? Math.max(digits, 2) : digits;
  return `${Number(p.toFixed(d))}%`;
};
const plural = (n, one, many) => Number(n) === 1 ? one : many;

/** "6 months" for 180, "12 months" for 365, "90 days" otherwise. */
export function periodText(days) {
  const d = Number(days) || 365;
  if (d >= 60) {
    const m = Math.round(d / 30.4);
    return `${m} ${plural(m, "month", "months")}`;
  }
  return `${d} days`;
}

/** Checklist rows derived from the health response. tone: good | warn | bad. */
function buildChecks(h, days, openPurge) {
  const rows = [];
  const inactive = Number(h.inactive) || 0;
  const legacy = Number(h.legacyAllowed) || 0;
  const dupes = Number(h.duplicatesPending) || 0;
  const sent = Number(h.sent) || 0;
  const flags = (h.flags || []).map(String);
  if (inactive > 0) {
    rows.push({
      k: "inactive",
      tone: "warn",
      title: `${num(inactive)} ${plural(inactive, "person hasn't", "people haven't")} opened, clicked, visited or ordered in ${periodText(days)}`,
      text: "Emailing them hurts how often your other email reaches the inbox. Ask them once if they still want to hear from you.",
      action: {
        label: "Ask if they still want email",
        onClick: openPurge
      }
    });
  }
  if (legacy > 0) {
    rows.push({
      k: "legacy",
      tone: "warn",
      title: `${num(legacy)} ${plural(legacy, "person has", "people have")} no email permission on record`,
      text: "They came from older imports. You can still show them ads. Ask before you email them.",
      action: {
        label: "See who",
        to: "/Audiences"
      }
    });
  }
  if (dupes > 0) {
    rows.push({
      k: "dupes",
      tone: "warn",
      title: `${num(dupes)} ${plural(dupes, "person may be", "people may be")} in your contacts twice`,
      text: "Merging them stops the same person getting the same email twice.",
      action: {
        label: "Review",
        to: "/Audiences/Duplicates"
      }
    });
  }
  const domainFlag = flags.find(f => /spf|dkim|dmarc|domain/i.test(f));
  if (domainFlag) {
    rows.push({
      k: "domain",
      tone: "bad",
      title: "Your sending domain needs a fix",
      text: "Mail providers can't confirm your email really comes from you, so more of it lands in spam.",
      action: {
        label: "Check domain",
        to: "/Email/senders"
      }
    });
  }
  if (sent > 0) {
    const c = frac(h.complaintRate);
    const cTone = c >= 0.003 ? "bad" : c >= 0.001 ? "warn" : "good";
    rows.push({
      k: "complaints",
      tone: cTone,
      title: cTone === "good" ? `Complaints are low: ${pctText(h.complaintRate, 2)} in the last ${RANGE} days` : `Complaints are ${cTone === "bad" ? "too high" : "rising"}: ${pctText(h.complaintRate, 2)} in the last ${RANGE} days`,
      text: cTone === "good" ? "Stay under 0.3%." : "Stay under 0.3%. Send only to people who asked for your email, and make unsubscribing easy."
    });
    const b = frac(h.bounceRate);
    const bTone = b >= 0.05 ? "bad" : b >= 0.02 ? "warn" : "good";
    rows.push({
      k: "bounces",
      tone: bTone,
      title: bTone === "good" ? `Bounces are low: ${pctText(h.bounceRate)} in the last ${RANGE} days` : `Too many emails bounce: ${pctText(h.bounceRate)} in the last ${RANGE} days`,
      text: bTone === "good" ? "Stay under 2%. Plunk stops emailing addresses that bounce." : "Stay under 2%. Plunk stops emailing addresses that bounce; older imports are the usual cause.",
      action: bTone === "good" ? null : {
        label: "See bounces",
        to: "/Audiences/Suppression"
      }
    });
  }
  if (h.deliverablePct != null && (Number(h.withEmail) || 0) > 0) {
    const dOk = frac(h.deliverablePct) >= 0.95;
    rows.push({
      k: "deliverable",
      tone: dOk ? "good" : "warn",
      title: dOk ? `${pctText(h.deliverablePct, 0)} of your email addresses work` : `Only ${pctText(h.deliverablePct, 0)} of your email addresses work`,
      text: dOk ? "Addresses that bounce or look wrong are skipped for you." : "The rest bounced, look wrong or asked to stop. Plunk skips them, but a clean list keeps you out of spam folders.",
      action: dOk ? null : {
        label: "See why",
        to: "/Audiences/Suppression"
      }
    });
  }
  flags.filter(f => !/spf|dkim|dmarc|domain|legacy_consent|bulk_sender_rules_apply|duplicates|complaint|bounce|deliverable/i.test(f)).forEach((f, i) => {
    const t = f.replace(/_/g, " ");
    rows.push({
      k: `flag-${i}`,
      tone: "warn",
      title: t.charAt(0).toUpperCase() + t.slice(1),
      text: "Flagged by last night's check."
    });
  });
  const rank = {
    bad: 0,
    warn: 1,
    good: 2
  };
  return rows.map((r, i) => ({
    ...r,
    i
  })).sort((x, y) => rank[x.tone] - rank[y.tone] || x.i - y.i);
}
function verdictText(checks) {
  const bad = checks.filter(c => c.tone === "bad").length;
  const warn = checks.filter(c => c.tone === "warn").length;
  if (bad > 0) {
    const n = bad + warn;
    return `${n} ${plural(n, "thing needs", "things need")} attention`;
  }
  if (warn > 0) return `Your list is in good shape, with ${warn} ${plural(warn, "thing", "things")} to look at`;
  return "Your list is in good shape";
}
function CheckRow({
  c,
  history
}) {
  const a = c.action;
  return /*#__PURE__*/React.createElement("div", {
    className: `ah-row ${c.tone}`
  }, /*#__PURE__*/React.createElement("span", {
    className: `ah-dot ${c.tone}`,
    "aria-label": c.tone === "good" ? "OK" : "Needs a look"
  }, c.tone === "good" ? "✓" : "!"), /*#__PURE__*/React.createElement("div", {
    className: "ah-row-text"
  }, /*#__PURE__*/React.createElement("b", null, c.title), /*#__PURE__*/React.createElement("span", null, c.text)), a ? /*#__PURE__*/React.createElement(Button, {
    className: "ah-act",
    onClick: () => a.onClick ? a.onClick() : history.push(a.to)
  }, a.label) : null);
}

/** Joined (adds) vs left (unsubscribes + suppressions) per week, drawn to one scale. */
const shortWeek = d => {
  const t = new Date(d + "T00:00:00");
  return isNaN(t) ? "" : t.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric"
  });
};
export function JoinedLeft({
  weekly
}) {
  const w = (weekly || []).slice(-12);
  if (w.length < 2) return /*#__PURE__*/React.createElement(Empty, null, "Weekly joins and departures appear once two weeks of history exist.");
  const rows = w.map(p => ({
    week: String(p.weekStart || "").slice(0, 10),
    joined: Number(p.adds) || 0,
    left: (Number(p.unsubscribes) || 0) + (Number(p.suppressions) || 0)
  }));
  const joined = rows.reduce((a, r) => a + r.joined, 0);
  const left = rows.reduce((a, r) => a + r.left, 0);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Chart, {
    width: "100%",
    height: "200px",
    chartType: "ColumnChart",
    data: [["Week", "Joined", "Left"], ...rows.map(r => [shortWeek(r.week), r.joined, r.left])],
    options: {
      colors: ["#ff6358", "#9fb3c8"],
      legend: {
        position: "top"
      },
      vAxis: {
        format: "short"
      },
      tooltip: {
        format: "{0:n0}"
      }
    }
  }), /*#__PURE__*/React.createElement("span", {
    className: "ah-cap"
  }, "+", num(joined), " joined and ", num(left), " left in the last ", rows.length, " weeks"));
}

/** One stacked bar: said yes / not on record / said no. */
export function PermissionBar({
  h
}) {
  const parts = [["yes", "Said yes", Number(h.optedIn) || 0], ["none", "Not on record", Number(h.legacyAllowed) || 0], ["no", "Said no", Number(h.optedOut) || 0]];
  const total = parts.reduce((a, p) => a + p[2], 0);
  if (!total) return /*#__PURE__*/React.createElement(Empty, null, "No email permission recorded yet.");
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: "ah-stack"
  }, parts.filter(p => p[2] > 0).map(([k, l, v]) => /*#__PURE__*/React.createElement("span", {
    key: k,
    className: k,
    style: {
      flex: v
    },
    title: `${l}: ${num(v)}`
  }))), /*#__PURE__*/React.createElement("div", {
    className: "ah-legend"
  }, parts.map(([k, l, v]) => /*#__PURE__*/React.createElement("span", {
    key: k
  }, /*#__PURE__*/React.createElement("i", {
    className: k
  }), l, " ", num(v)))));
}
class PurgeWizard extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "preview", async () => {
      if (Number(this.state.days) < 90) {
        this.setState({
          err: "The shortest window is 90 days."
        });
        return;
      }
      this.setState({
        busy: true,
        err: "",
        preview: null
      });
      try {
        const r = await purgeInactive(this.body("preview"));
        if (this._alive) this.setState({
          preview: r
        });
      } catch (e) {
        if (this._alive) this.setState({
          err: e.message || "Couldn't count who matches"
        });
      } finally {
        if (this._alive) this.setState({
          busy: false
        });
      }
    });
    _defineProperty(this, "run", async () => {
      const {
        choice,
        preview
      } = this.state;
      if (choice === "email") {
        // The "still want to hear from us?" email is created in the Email section;
        // we only hand over the preview's personIds. No campaign is created here.
        this.setState({
          result: {
            mode: "email",
            personIds: preview && preview.personIds || [],
            count: preview ? Number(preview.candidates) - Number(preview.keptRecentOrder || 0) - Number(preview.keptBusiness || 0) : 0
          }
        });
        return;
      }
      this.setState({
        busy: true,
        err: ""
      });
      try {
        const r = await purgeInactive(this.body("demote"));
        if (!this._alive) return;
        this.setState({
          result: r
        });
        toast(`${num(r.demoted)} people set aside.`);
        this.props.onDone && this.props.onDone();
      } catch (e) {
        if (this._alive) this.setState({
          err: e.message || "Couldn't set them aside"
        });
      } finally {
        if (this._alive) this.setState({
          busy: false
        });
      }
    });
    this.state = {
      days: props.days || 365,
      keepOrders: true,
      keepMonths: 24,
      keepBusiness: true,
      preview: null,
      busy: false,
      err: "",
      choice: "email",
      result: null
    };
    this._alive = true;
  }
  componentDidMount() {
    this.preview();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  body(mode) {
    return {
      days: Math.max(90, Number(this.state.days) || 365),
      keepRecentOrderMonths: this.state.keepOrders ? Number(this.state.keepMonths) || 24 : null,
      keepBusiness: !!this.state.keepBusiness,
      mode
    };
  }
  render() {
    const {
      days,
      keepOrders,
      keepMonths,
      keepBusiness,
      preview,
      busy,
      err,
      choice,
      result
    } = this.state;
    const toMove = preview ? Math.max(0, Number(preview.candidates) - Number(preview.keptRecentOrder || 0) - Number(preview.keptBusiness || 0) - Number(preview.alreadyInactive || 0)) : null;
    const inactiveLink = preview && preview.inactiveAudienceId ? `/Audiences/Detail/${encodeURIComponent(String(preview.inactiveAudienceId))}/this_month` : null;
    const title = "Set aside people who stopped engaging";
    if (result) {
      return /*#__PURE__*/React.createElement(Modal, {
        title: title,
        width: 620,
        onClose: this.props.onClose,
        footer: /*#__PURE__*/React.createElement(Button, {
          themeColor: "primary",
          onClick: this.props.onClose
        }, "Done")
      }, result.mode === "email" ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("h4", {
        className: "aud-h4"
      }, "Ask them first"), /*#__PURE__*/React.createElement("p", null, "Send ", /*#__PURE__*/React.createElement("b", null, num(result.count)), " people one email titled ", /*#__PURE__*/React.createElement("b", null, "\"Still want to hear from us?\""), " with a single button. Anyone who clicks stays on your lists; the rest are set aside 7 days after it goes out."), /*#__PURE__*/React.createElement(Check, {
        m: "warn"
      }, "Nothing has changed yet. To send it, open Email, start a new campaign and pick the ", /*#__PURE__*/React.createElement("b", null, "Inactive candidates"), " list.")) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("h4", {
        className: "aud-h4"
      }, "Done"), /*#__PURE__*/React.createElement("div", {
        className: "aud-funnel"
      }, /*#__PURE__*/React.createElement(FunnelRow, {
        label: "Matched",
        value: num(result.candidates)
      }), /*#__PURE__*/React.createElement(FunnelRow, {
        cls: "minus",
        label: "kept: ordered recently",
        value: `−${num(result.keptRecentOrder)}`
      }), /*#__PURE__*/React.createElement(FunnelRow, {
        cls: "minus",
        label: "kept: business contacts",
        value: `−${num(result.keptBusiness)}`
      }), /*#__PURE__*/React.createElement(FunnelRow, {
        cls: "minus",
        label: "already set aside",
        value: `−${num(result.alreadyInactive)}`
      }), /*#__PURE__*/React.createElement(FunnelRow, {
        cls: "total",
        label: "Set aside now",
        value: num(result.demoted)
      }), Number(result.reactivated) > 0 ? /*#__PURE__*/React.createElement(FunnelRow, {
        label: "Came back and were restored",
        value: num(result.reactivated)
      }) : null, /*#__PURE__*/React.createElement(FunnelRow, {
        label: "On the Inactive list now",
        value: num(result.inactiveTotal)
      })), (result.notes || []).map((n, i) => /*#__PURE__*/React.createElement("div", {
        className: "ec-help",
        key: i
      }, n)), result.inactiveAudienceId ? /*#__PURE__*/React.createElement("div", {
        className: "ec-help"
      }, /*#__PURE__*/React.createElement(Link, {
        to: `/Audiences/Detail/${encodeURIComponent(String(result.inactiveAudienceId))}/this_month`
      }, "Open the Inactive list")) : null));
    }
    return /*#__PURE__*/React.createElement(Modal, {
      title: title,
      width: 900,
      onClose: this.props.onClose,
      busy: busy,
      note: "Nothing is deleted. Orders and history stay.",
      footer: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Button, {
        onClick: this.props.onClose,
        disabled: busy
      }, "Cancel"), /*#__PURE__*/React.createElement(Button, {
        themeColor: "primary",
        disabled: busy || !preview || !toMove,
        onClick: this.run
      }, choice === "email" ? "Ask them first" : busy ? "Setting aside…" : `Set aside ${toMove != null ? num(toMove) : ""} people now`))
    }, /*#__PURE__*/React.createElement("div", {
      className: "aud-2col"
    }, /*#__PURE__*/React.createElement("div", {
      className: "aud-panel"
    }, /*#__PURE__*/React.createElement("h3", {
      className: "aud-h3"
    }, "1 \xB7 Who counts as not engaged"), /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ec-inline"
    }, "No open, click, order, site visit or form in the last ", /*#__PURE__*/React.createElement("input", {
      type: "number",
      min: "90",
      className: "ec-input ec-num ah-num",
      value: days,
      onChange: e => this.setState({
        days: e.target.value
      }),
      onBlur: this.preview
    }), " days"), /*#__PURE__*/React.createElement("div", {
      className: "ec-help"
    }, "The shortest window is 90 days.")), /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", {
      className: "ec-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: keepOrders,
      onChange: e => this.setState({
        keepOrders: e.target.checked
      }, this.preview)
    }), /*#__PURE__*/React.createElement("span", {
      className: "ec-inline"
    }, "Keep anyone who ordered in the last ", /*#__PURE__*/React.createElement("input", {
      type: "number",
      min: "1",
      className: "ec-input ec-num ah-num",
      value: keepMonths,
      disabled: !keepOrders,
      onChange: e => this.setState({
        keepMonths: e.target.value
      }),
      onBlur: this.preview
    }), " months")), /*#__PURE__*/React.createElement("label", {
      className: "ec-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "checkbox",
      checked: keepBusiness,
      onChange: e => this.setState({
        keepBusiness: e.target.checked
      }, this.preview)
    }), /*#__PURE__*/React.createElement("span", null, "Keep business contacts (partners, resellers, reps)"))), /*#__PURE__*/React.createElement("h4", {
      className: "aud-h4"
    }, "Who matches"), busy && !preview ? /*#__PURE__*/React.createElement(Pending, {
      h: 100
    }) : preview ? /*#__PURE__*/React.createElement("div", {
      className: "aud-funnel"
    }, /*#__PURE__*/React.createElement(FunnelRow, {
      label: "Not engaged",
      value: num(preview.candidates)
    }), /*#__PURE__*/React.createElement(FunnelRow, {
      cls: "minus",
      label: "kept: ordered recently",
      value: `−${num(preview.keptRecentOrder)}`
    }), /*#__PURE__*/React.createElement(FunnelRow, {
      cls: "minus",
      label: "kept: business contacts",
      value: `−${num(preview.keptBusiness)}`
    }), Number(preview.alreadyInactive) > 0 ? /*#__PURE__*/React.createElement(FunnelRow, {
      cls: "minus",
      label: "already set aside",
      value: `−${num(preview.alreadyInactive)}`
    }) : null, /*#__PURE__*/React.createElement(FunnelRow, {
      cls: "total",
      label: "Will be set aside",
      value: num(toMove)
    })) : null, /*#__PURE__*/React.createElement(ErrorLine, null, err), (preview && preview.notes || []).map((n, i) => /*#__PURE__*/React.createElement("div", {
      className: "ec-help",
      key: i
    }, n))), /*#__PURE__*/React.createElement("div", {
      className: "aud-panel"
    }, /*#__PURE__*/React.createElement("h3", {
      className: "aud-h3"
    }, "2 \xB7 Ask them once"), /*#__PURE__*/React.createElement("div", {
      className: "ec-field"
    }, /*#__PURE__*/React.createElement("label", {
      className: "ec-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "radio",
      name: "purge-choice",
      checked: choice === "email",
      onChange: () => this.setState({
        choice: "email"
      })
    }), /*#__PURE__*/React.createElement("span", null, "Email them first. Anyone who clicks stays.")), /*#__PURE__*/React.createElement("div", {
      className: "ec-help aud-indent"
    }, "One short email, \"Still want to hear from us?\", with a single button. The rest are set aside 7 days after it goes out."), /*#__PURE__*/React.createElement("label", {
      className: "ec-check"
    }, /*#__PURE__*/React.createElement("input", {
      type: "radio",
      name: "purge-choice",
      checked: choice === "demote",
      onChange: () => this.setState({
        choice: "demote"
      })
    }), /*#__PURE__*/React.createElement("span", null, "Set them aside now, without an email."))), /*#__PURE__*/React.createElement("h4", {
      className: "aud-h4"
    }, "3 \xB7 What happens"), /*#__PURE__*/React.createElement(Check, {
      m: "ok"
    }, toMove != null ? num(toMove) : "Matching", " people move to the ", /*#__PURE__*/React.createElement("b", null, "Inactive"), " list. Bulk email skips them; automated journeys still reach them", inactiveLink ? /*#__PURE__*/React.createElement(React.Fragment, null, " \xB7 ", /*#__PURE__*/React.createElement(Link, {
      to: inactiveLink
    }, "open the list")) : null), /*#__PURE__*/React.createElement(Check, {
      m: "ok"
    }, "They are removed from connected email tools (Mailchimp, Constant Contact) on the next sync"), /*#__PURE__*/React.createElement(Check, {
      m: "ok"
    }, "Nothing is deleted. Anyone who comes back is restored automatically"))), /*#__PURE__*/React.createElement("div", {
      className: "aud-note"
    }, "To remove someone completely after a privacy request, an Administrator can erase them from their contact page."));
  }
}
class HealthComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "saveInactivity", async () => {
      if (Number(this.state.inactivityDraft) < 90) {
        toast("The shortest window is 90 days.");
        return;
      }
      this.setState({
        savingDays: true
      });
      try {
        const r = await putInactivityDays(this.state.inactivityDraft);
        this.setState({
          inactivityDays: r.inactivityDays,
          inactivityDraft: String(r.inactivityDays),
          savingDays: false
        });
        toast("Saved.");
        this.load();
      } catch (e) {
        this.setState({
          savingDays: false
        });
        toast(e && e.message ? e.message : "Save failed");
      }
    });
    this.state = {
      data: null,
      error: false,
      purge: false,
      inactivityDays: null,
      inactivityDraft: "",
      savingDays: false
    };
    this._alive = true;
  }
  componentDidMount() {
    this.load();
    getEmailSettings().then(es => {
      if (this._alive && es) this.setState({
        inactivityDays: es.inactivityDays || 365,
        inactivityDraft: String(es.inactivityDays || 365)
      });
    }).catch(() => {});
  }
  componentWillUnmount() {
    this._alive = false;
  }
  async load() {
    this.setState({
      data: null
    });
    try {
      const r = await getHealth(RANGE);
      if (this._alive) this.setState({
        data: r
      });
    } catch (e) {
      if (this._alive) this.setState({
        error: true
      });
    }
  }
  renderSetting() {
    const {
      inactivityDays,
      inactivityDraft,
      savingDays
    } = this.state;
    const admin = isAdministrator();
    return /*#__PURE__*/React.createElement("div", {
      className: "ah-setting"
    }, /*#__PURE__*/React.createElement("span", null, "Count someone as not engaged after"), admin ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("input", {
      type: "number",
      min: "90",
      className: "ah-num",
      "aria-label": "Days without activity",
      value: inactivityDraft,
      onChange: e => this.setState({
        inactivityDraft: e.target.value
      })
    }), /*#__PURE__*/React.createElement("span", null, "days"), String(inactivityDays) !== String(inactivityDraft) && inactivityDays != null ? /*#__PURE__*/React.createElement(Button, {
      themeColor: "primary",
      size: "small",
      disabled: savingDays,
      onClick: this.saveInactivity
    }, savingDays ? "Saving…" : "Save") : null) : /*#__PURE__*/React.createElement("b", null, inactivityDays || 365, " days"), /*#__PURE__*/React.createElement("span", {
      className: "ah-setting-help"
    }, admin ? "At least 90 days. Also under Email settings." : "Only an Administrator can change this."));
  }
  render() {
    const {
      data: h,
      error,
      purge,
      inactivityDays
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, null);
    const days = inactivityDays || 365;
    const checks = h ? buildChecks(h, days, () => this.setState({
      purge: true
    })) : [];
    return /*#__PURE__*/React.createElement(AudiencesShell, {
      subtitle: SUBTITLES.health,
      cls: "ah-view"
    }, /*#__PURE__*/React.createElement("section", {
      className: "ah-card ah-summary"
    }, !h ? /*#__PURE__*/React.createElement(Pending, {
      h: 48
    }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "ah-verdict"
    }, /*#__PURE__*/React.createElement("b", null, verdictText(checks)), /*#__PURE__*/React.createElement("span", null, "Checked every night. Fixing these keeps your email out of spam folders.")), /*#__PURE__*/React.createElement("span", {
      className: "ah-stat"
    }, /*#__PURE__*/React.createElement("b", null, num(h.deliverable)), /*#__PURE__*/React.createElement("span", null, "can get email")), /*#__PURE__*/React.createElement("span", {
      className: "ah-stat"
    }, /*#__PURE__*/React.createElement("b", null, pctText(h.deliverablePct, 0)), /*#__PURE__*/React.createElement("span", null, "of addresses work")), /*#__PURE__*/React.createElement("span", {
      className: "ah-stat"
    }, /*#__PURE__*/React.createElement("b", null, num(h.inactive)), /*#__PURE__*/React.createElement("span", null, "haven't engaged in ", periodText(days))))), /*#__PURE__*/React.createElement("div", {
      className: "ah-grid"
    }, /*#__PURE__*/React.createElement("section", {
      className: "ah-card ah-checklist"
    }, /*#__PURE__*/React.createElement("b", {
      className: "ah-title"
    }, "Checklist"), !h ? /*#__PURE__*/React.createElement(Pending, null) : checks.length === 0 ? /*#__PURE__*/React.createElement(Empty, null, "Nothing to check yet. Checks appear once you have contacts with email addresses.") : checks.map(c => /*#__PURE__*/React.createElement(CheckRow, {
      key: c.k,
      c: c,
      history: this.props.history
    })), h && (h.notes || []).length ? /*#__PURE__*/React.createElement("p", {
      className: "ah-notes"
    }, h.notes.join(" ")) : null), /*#__PURE__*/React.createElement("div", {
      className: "ah-side"
    }, /*#__PURE__*/React.createElement("section", {
      className: "ah-card"
    }, /*#__PURE__*/React.createElement("div", {
      className: "ah-head"
    }, /*#__PURE__*/React.createElement("b", {
      className: "ah-title"
    }, "Joined and left, by week")), !h ? /*#__PURE__*/React.createElement(Pending, null) : /*#__PURE__*/React.createElement(JoinedLeft, {
      weekly: h.weekly
    })), /*#__PURE__*/React.createElement("section", {
      className: "ah-card"
    }, /*#__PURE__*/React.createElement("b", {
      className: "ah-title"
    }, "Email permission"), !h ? /*#__PURE__*/React.createElement(Pending, null) : /*#__PURE__*/React.createElement(PermissionBar, {
      h: h
    })), this.renderSetting())), purge ? /*#__PURE__*/React.createElement(PurgeWizard, {
      days: days,
      onClose: () => this.setState({
        purge: false
      }),
      onDone: () => this.load()
    }) : null);
  }
}
export default withRouter(HealthComponent);